<template>
  <div v-loading="loading" element-loading-spinner="atom-audit-loader">
    <!-- <h3>{{group.key}}</h3> -->
    <div v-for="(item, index) in allData" :key="index" :class="{ 'provider-section': index > 0 }">
      <h2>{{ item.providerName }}</h2>
      <table class="hiaTable proProviderWorksheetSummaryTable" style="margin-top: 20px;">
        <colgroup>
          <col style="width: 10%;" />
          <col style="width: 10%;" />
          <col style="width: 12%;" />
          <col :style="hasDxLevel2() ? 'width: 270px;' : 'width: 185px;'" />
          <col :style="hasCptLevel2() ? 'width: 270px;' : 'width: 185px;'" />
          <col style="width: 10%;" />
          <col style="width: 20%;" />
        </colgroup>
        <thead>
          <tr>
            <th>Patient Number</th>
            <th>Payor</th>
            <th>Date of Service</th>
            <th>Dx Coding</th>
            <th>CPT Coding</th>
            <th>Reason for Change</th>
            <th>{{ tenantSetup.enableProKeyFindings ? 'Key Findings' : 'Action Taken' }}</th>
          </tr>
        </thead>
        <tr v-for="(item, index) in item.records" :key="index">
          <td class="textAlignLeft">{{ item.patientNumber }}</td>
          <td class="textAlignLeft">{{ item.payorName }}</td>
          <td class="textAlignLeft">{{ item.dateOfService ? format(parseISO(item.dateOfService), 'M/d/yyyy') : '' }}</td>
          <td>
            <template v-if="item.isLevel2">
              <table class="dx-coding-table">
                <tr>
                  <td class="dx-sub-column" v-if="showPreDxCodeType(item)">
                    <div class="codeTypeHeader" :title="item.dxs.length > 0 ? item.dxs[0].preDxCodeDesc : ''">
                      {{ item.dxs.length > 0 ? item.dxs[0].preDxCodeDesc : '' }}
                    </div>
                  </td>
                  <td class="dx-sub-column" v-if="showOriginalDxCodeType(item)">
                    <div class="codeTypeHeader" :title="item.dxs.length > 0 ? item.dxs[0].codeDesc : ''">
                      {{ item.dxs.length > 0 ? item.dxs[0].codeDesc : '' }}
                    </div>
                  </td>
                  <td class="dx-sub-column">
                    <div class="codeTypeHeader" title="Recommended">
                      Recommended
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="dx-sub-column" v-if="showPreDxCodeType(item)">
                    <ol class="code-list">
                      <li v-for="(dx, index) in item.dxs" :key="index" :class="{ 'red-font': isPreDxRedFont(dx), bold: isDxRowChange(dx) }">
                        {{ dx.preDx ? dx.preDx : '' }}
                      </li>
                    </ol>
                  </td>

                  <td class="dx-sub-column" v-if="showOriginalDxCodeType(item)">
                    <ol class="code-list">
                      <li v-for="(dx, index) in item.dxs" :key="index" :class="{ 'red-font': isOriginalDxRedFont(dx), bold: isDxRowChange(dx) }">
                        {{ dx.preDxDeleted ? 'DEL' : dx.originalDx ? dx.originalDx : '' }}
                      </li>
                    </ol>
                  </td>

                  <td class="dx-sub-column">
                    <ol class="code-list">
                      <li v-for="(dx, index) in item.dxs" :key="index" :class="{ 'red-font': isRecommendedDxRedFont(dx), bold: isDxRowChange(dx) }">
                        {{ dx.originalDeleted ? 'DEL' : dx.recommendedDx ? dx.recommendedDx : '' }}
                      </li>
                    </ol>
                  </td>
                </tr>
              </table>
            </template>
            <template v-else>
              <table class="dx-coding-table" v-for="codeType in getCodeTypes(item.dxs)" :key="codeType">
                <tr>
                  <td class="dx-sub-column">
                    <div class="codeTypeHeader" :title="codeType">
                      {{ codeType }}
                    </div>
                  </td>
                  <td class="dx-sub-column">
                    <div class="codeTypeHeader" title="Recommended">
                      Recommended
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="dx-sub-column">
                    <ol class="code-list">
                      <li v-for="(dx, index) in item.dxs.filter(x => x.codeDesc === codeType)" :key="index" :class="{ 'red-font': dx.originalDeleted, bold: isDxRowChange(dx) }">
                        {{ dx.originalDx ? dx.originalDx : '' }}
                      </li>
                    </ol>
                  </td>
                  <td class="dx-sub-column">
                    <ol class="code-list">
                      <li v-for="(dx, index) in item.dxs.filter(x => x.codeDesc === codeType)" :key="index" :class="{ 'red-font': dx.originalDeleted, bold: isDxRowChange(dx) }">
                        {{ dx.originalDeleted ? 'DEL' : dx.recommendedDx ? dx.recommendedDx : '' }}
                      </li>
                    </ol>
                  </td>
                </tr>
              </table>
            </template>
          </td>
          <td>
            <template v-if="item.isCptLevel2">
              <table class="cpt-coding-table">
                <tr>
                  <td class="cpt-sub-column" v-if="showPreCptCodeType(item)">
                    <div class="codeTypeHeader" :title="item.cpts.length > 0 ? item.cpts[0].preCptCodeDesc : ''">
                      {{ item.cpts.length > 0 ? item.cpts[0].preCptCodeDesc : '' }}
                    </div>
                  </td>
                  <td class="cpt-sub-column" v-if="showOriginalCptCodeType(item)">
                    <div class="codeTypeHeader" :title="item.cpts.length > 0 ? item.cpts[0].codeDesc : ''">
                      {{ item.cpts.length > 0 ? item.cpts[0].codeDesc : '' }}
                    </div>
                  </td>
                  <td class="cpt-sub-column">
                    <div class="codeTypeHeader" title="Recommended">
                      Recommended
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="cpt-sub-column" v-if="showPreCptCodeType(item)">
                    <ol class="code-list">
                      <li v-for="(cpt, index) in item.cpts" :key="index" :class="{ 'red-font': isPreCptRedFont(cpt), bold: isCptRowChange(cpt) }">
                        {{ cpt.preCpt ? cpt.preCpt : '' }}
                      </li>
                    </ol>
                  </td>

                  <td class="cpt-sub-column" v-if="showOriginalCptCodeType(item)">
                    <ol class="code-list">
                      <li v-for="(cpt, index) in item.cpts" :key="index" :class="{ 'red-font': isOriginalCptRedFont(cpt), bold: isCptRowChange(cpt) }">
                        {{ cpt.preCptDeleted ? 'DEL' : getOriginalCpt(cpt) }}
                      </li>
                    </ol>
                  </td>

                  <td class="cpt-sub-column">
                    <ol class="code-list">
                      <li v-for="(cpt, index) in item.cpts" :key="index" :class="{ 'red-font': isRecommendedCptRedFont(cpt), bold: isCptRowChange(cpt) }" v-html="getRecommendedCpt(cpt)"></li>
                    </ol>
                  </td>
                </tr>
              </table>
            </template>
            <template v-else>
              <table class="cpt-coding-table" v-for="codeType in getCodeTypes(item.cpts)" :key="codeType">
                <tr>
                  <td class="cpt-sub-column">
                    <div class="codeTypeHeader" :title="codeType">
                      {{ codeType }}
                    </div>
                  </td>
                  <td class="cpt-sub-column">
                    <div class="codeTypeHeader" title="Recommended">
                      Recommended
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="cpt-sub-column">
                    <ol class="code-list">
                      <li v-for="(cpt, index) in item.cpts.filter(x => x.codeDesc === codeType)" :key="index" :class="{ 'red-font': cpt.originalDeleted, bold: isCptRowChange(cpt) }">
                        {{ getOriginalCpt(cpt) }}
                      </li>
                    </ol>
                  </td>
                  <td class="cpt-sub-column">
                    <ol class="code-list">
                      <li v-for="(cpt, index) in item.cpts.filter(x => x.codeDesc === codeType)" :key="index" v-html="getRecommendedCpt(cpt)" :class="{ 'red-font': cpt.originalDeleted, bold: isCptRowChange(cpt) }"></li>
                    </ol>
                  </td>
                </tr>
              </table>
            </template>
          </td>
          <td class="textAlignLeft">{{ item.reasonForChange }}</td>
          <td class="textAlignLeft key-findings" style="white-space: pre-wrap;">
            <div style="white-space: pre-wrap;">{{ getKeyFindings(item) }}</div>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex'
  import axios from '@/utilities/axios-global'
  import hiaFilters from '@/mixins/hiaFilters'
  import money from '@/mixins/money'
  import dashboardHelper from '@/mixins/dashboardHelper'
  import hiaAuthorization from '@/mixins/hiaAuthorization'
  import { format, parseISO } from 'date-fns'

  export default {
    name: 'PRO_ProviderSummaryPrintable',
    mixins: [hiaFilters, money, dashboardHelper, hiaAuthorization],
    props: ['proRecordIds', 'codeTypes'],
    components: {},
    data: function() {
      return {
        format: format,
        parseISO: parseISO,
        allData: [],
        filterCriteria: '',
        loading: false,
        summaryRow: {},
        detailsHeader: '',
        localCodeTypes: []
      }
    },
    methods: {
      ...mapActions('managedLists/tenantSetup/', ['GET_TENANT_SETUP']),
      async getData() {
        const payload = {
          ids: this.proRecordIds,
          codeTypes: this.localCodeTypes
        }

        try {
          this.loading = true
          let response

          if (this.providerReviewer()) {
            response = await axios.post(`${this.baseApiUrl}/reviews/pro/worksheets?changesOnly=true`, payload)
          }

          if (!this.providerReviewer() && this.coder()) {
            response = await axios.post(`${this.baseApiUrl}/coder/pro/worksheets?changesOnly=true`, payload)
          }

          const uniqueProviders = [...new Set(response.data.map(item => item.physicianName))]
          uniqueProviders.sort((a, b) => (a > b ? 1 : -1))
          const allData = []
          uniqueProviders.forEach(provider => {
            const providerData = response.data.filter(item => item.physicianName === provider)
            allData.push({
              providerName: provider,
              records: providerData
            })
          })
          this.allData = allData
        } catch (err) {
          this.allData = []
        } finally {
          this.$nextTick(() => {
            // this.$refs.dxTable.doLayout()
            this.loading = false
          })
        }
      },
      getCodeTypes(arr) {
        const codeTypes = [...new Set(arr.map(x => x.codeDesc))]
        return codeTypes.sort((a, b) => (a > b ? 1 : -1))
      },
      getKeyFindings(row) {
        if (this.tenantSetup.enableProKeyFindings) {
          // Convert HTML to plain text, preserving line breaks
          return this.htmlToPlainText(row.keyFindings)
        }
        // For findings array, join each findingDetails as plain text
        return row.findings.map(x => this.htmlToPlainText(x.findingDetails)).join('\n\n')
      },
      htmlToPlainText(html) {
        if (!html) return ''
        // Replace <br> and <br/> with newlines
        let text = html.replace(/<br\s*\/?>/gi, '\n')
        // Remove all other HTML tags
        text = text.replace(/<[^>]+>/g, '')
        // decode HTML entities
        const txt = document.createElement('textarea')
        txt.innerHTML = text
        let result = txt.value
        // Remove trailing newlines (but not those in the middle)
        result = result.replace(/[\n\r]+$/g, '')
        return result
      },
      cellClasses({ row, column, rowIndex, columnIndex }) {
        if (column.property === 'code' || column.property === 'description' || column.property === 'codeDesc') {
          return 'textAlignLeft'
        }
        if (column.property === 'details') {
          return 'textAlignCenter'
        }
      },
      getOriginalCpt(cpt) {
        if (!cpt.originalCPT) return ''
        const modifier = cpt.originalModifierCalc || ''
        return cpt.originalCPT + modifier
      },
      getRecommendedCpt(cpt) {
        if (cpt.originalDeleted) {
          return 'DEL'
        } else {
          return cpt.calcRecCPT
        }
      },
      isDxRowChange(dx) {
        if (dx.recommendedDx || dx.originalDeleted) {
          return true
        }

        if (((this.localCodeTypes.length > 0 && this.localCodeTypes.includes(dx.preDxCodeType)) || this.localCodeTypes.length === 0) && (dx.preDxDeleted || dx.originalDx)) {
          return true
        }

        return false
      },
      isCptRowChange(cpt) {
        if (cpt.recommendedCpt || cpt.originalDeleted) {
          return true
        }

        if (((this.localCodeTypes.length > 0 && this.localCodeTypes.includes(cpt.preCptCodeType)) || this.localCodeTypes.length === 0) && (cpt.preCptDeleted || cpt.originalCPT)) {
          return true
        }

        return false
      },
      showPreDxCodeType(row) {
        if (row.dxs.length > 0 && this.localCodeTypes.length > 0) {
          return this.localCodeTypes.includes(row.dxs[0].preDxCodeType)
        }
        return true
      },
      showOriginalDxCodeType(row) {
        if (row.dxs.length > 0 && this.localCodeTypes.length > 0) {
          return this.localCodeTypes.includes(row.dxs[0].codeType)
        }
        return true
      },
      showPreCptCodeType(row) {
        if (row.cpts.length > 0 && this.localCodeTypes.length > 0) {
          return this.localCodeTypes.includes(row.cpts[0].preCptCodeType)
        }
        return true
      },
      showOriginalCptCodeType(row) {
        if (row.cpts.length > 0 && this.localCodeTypes.length > 0) {
          return this.localCodeTypes.includes(row.cpts[0].codeType)
        }
        return true
      },
      hasDxLevel2() {
        return this.allData.some(x => x.records.some(y => y.isLevel2))
      },
      hasCptLevel2() {
        return this.allData.some(x => x.records.some(y => y.isCptLevel2))
      },
      isPreDxRedFont(dx, dxs) {
        if (dx.preDxDeleted || (dx.originalDeleted && !dx.originalDx)) {
          return true
        }
        return false
      },
      isOriginalDxRedFont(dx) {
        if (dx.originalDeleted || dx.preDxDeleted) {
          return true
        }
        return false
      },
      isRecommendedDxRedFont(dx) {
        return dx.originalDeleted || dx.preDxDeleted
      },
      isPreCptRedFont(cpt, cpts) {
        if (cpt.preCptDeleted || (cpt.originalDeleted && !cpt.originalCPT)) {
          return true
        }
        return false
      },
      isOriginalCptRedFont(cpt) {
        if (cpt.originalDeleted || cpt.preCptDeleted) {
          return true
        }
        return false
      },
      isRecommendedCptRedFont(cpt) {
        return cpt.originalDeleted || cpt.preCptDeleted
      }
    },
    created() {
      this.localCodeTypes = this.codeTypes.map(x => parseInt(x))
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      ...mapState('managedLists/tenantSetup/', ['tenantSetup'])
    },
    mounted: async function() {
      await this.GET_TENANT_SETUP()
      await this.getData()
    },
    watch: {
      proRecordIds: async function() {
        await this.getData()
      }
    }
  }
</script>

<style scoped>
  .no-border-right {
    border-right: none !important;
  }

  .no-border-left {
    border-left: none;
  }

  .provider-section:not(:first-child) {
    margin-top: 50px;
  }

  .line-through {
    text-decoration: line-through;
  }

  .code-list {
    margin: 0;
    padding-left: 0px;
    list-style-position: inside;
    margin: 4px 0px 9px 0px;
  }

  .code-list li {
    padding-left: 0;
    margin: 3px 0px;
  }

  >>> .delCode {
    color: red;
    text-decoration: line-through;
  }

  .red-font {
    color: red;
  }

  .bold {
    font-weight: bold;
  }

  .codeTypeHeader {
    text-align: left;
    font-style: italic;
    margin-bottom: 4px;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    width: 100%;
    min-width: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: none;
  }

  .proProviderWorksheetSummaryTable {
    table-layout: fixed;
    width: 100%;
  }

  .dx-coding-table,
  .cpt-coding-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    margin: 0;
    padding: 0;
    background: transparent !important;
    border: none !important;
    border-spacing: 0 !important;
  }

  .dx-sub-column,
  .cpt-sub-column {
    width: 33.33%;
    vertical-align: top;
    padding: 0 3px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 0;
    background: transparent !important;
    border: none !important;
    border-top: none !important;
    border-bottom: none !important;
    border-left: none !important;
    border-right: none !important;
  }

  /* Override any inherited table styles */
  .proProviderWorksheetSummaryTable .dx-coding-table,
  .proProviderWorksheetSummaryTable .cpt-coding-table {
    background: transparent !important;
    border: none !important;
  }

  .proProviderWorksheetSummaryTable .dx-sub-column,
  .proProviderWorksheetSummaryTable .cpt-sub-column {
    background: transparent !important;
    border: none !important;
    border-top: none !important;
    border-bottom: none !important;
    border-left: none !important;
    border-right: none !important;
  }

  /* Override Bootstrap table styles if present */
  .proProviderWorksheetSummaryTable table.dx-coding-table,
  .proProviderWorksheetSummaryTable table.cpt-coding-table {
    background: transparent !important;
    border: none !important;
  }

  .proProviderWorksheetSummaryTable table.dx-coding-table td,
  .proProviderWorksheetSummaryTable table.cpt-coding-table td {
    background: transparent !important;
    border: none !important;
    border-top: none !important;
    border-bottom: none !important;
    border-left: none !important;
    border-right: none !important;
  }

  /* Override the hiaTable odd row background for sub-tables */
  .proProviderWorksheetSummaryTable .dx-coding-table tr,
  .proProviderWorksheetSummaryTable .cpt-coding-table tr {
    background: transparent !important;
  }

  .proProviderWorksheetSummaryTable .dx-coding-table tr:nth-child(odd),
  .proProviderWorksheetSummaryTable .cpt-coding-table tr:nth-child(odd) {
    background: transparent !important;
  }

  .proProviderWorksheetSummaryTable .dx-coding-table tr:nth-child(even),
  .proProviderWorksheetSummaryTable .cpt-coding-table tr:nth-child(even) {
    background: transparent !important;
  }

  /* Override inherited table cell borders */
  .proProviderWorksheetSummaryTable .dx-coding-table td,
  .proProviderWorksheetSummaryTable .cpt-coding-table td {
    border-left: none !important;
    border-right: none !important;
    border-top: none !important;
    border-bottom: none !important;
  }

  /* Override the specific proAuditReport hiaTable td rule */
  #proAuditReport .proProviderWorksheetSummaryTable .dx-coding-table td,
  #proAuditReport .proProviderWorksheetSummaryTable .cpt-coding-table td {
    border-left: none !important;
    border-right: none !important;
    border-top: none !important;
    border-bottom: none !important;
  }
</style>

<style>
  @media print {
    .provider-section {
      page-break-before: always;
      break-before: page;
    }
    .key-findings {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    /* Ensure table cells don't overflow in print */
    .proProviderWorksheetSummaryTable {
      table-layout: fixed !important;
      width: 100% !important;
    }

    .proProviderWorksheetSummaryTable td {
      overflow: hidden !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }

    /* Fix Dx and CPT coding tables for print */
    .dx-coding-table,
    .cpt-coding-table {
      table-layout: fixed !important;
      width: 100% !important;
      border-collapse: collapse !important;
      background: transparent !important;
      border: none !important;
    }

    .dx-sub-column,
    .cpt-sub-column {
      width: auto !important;
      max-width: none !important;
      overflow: hidden !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      background: transparent !important;
      border: none !important;
    }

    /* Override the hiaTable odd row background for sub-tables in print */
    .proProviderWorksheetSummaryTable .dx-coding-table tr,
    .proProviderWorksheetSummaryTable .cpt-coding-table tr {
      background: transparent !important;
    }

    .proProviderWorksheetSummaryTable .dx-coding-table tr:nth-child(odd),
    .proProviderWorksheetSummaryTable .cpt-coding-table tr:nth-child(odd) {
      background: transparent !important;
    }

    .proProviderWorksheetSummaryTable .dx-coding-table tr:nth-child(even),
    .proProviderWorksheetSummaryTable .cpt-coding-table tr:nth-child(even) {
      background: transparent !important;
    }

    /* Override inherited table cell borders in print */
    .proProviderWorksheetSummaryTable .dx-coding-table td,
    .proProviderWorksheetSummaryTable .cpt-coding-table td {
      border-left: none !important;
      border-right: none !important;
      border-top: none !important;
      border-bottom: none !important;
    }

    /* Override the specific proAuditReport hiaTable td rule in print */
    #proAuditReport .proProviderWorksheetSummaryTable .dx-coding-table td,
    #proAuditReport .proProviderWorksheetSummaryTable .cpt-coding-table td {
      border-left: none !important;
      border-right: none !important;
      border-top: none !important;
      border-bottom: none !important;
    }

    /* Ensure code lists don't overflow */
    .code-list {
      margin: 0 !important;
      padding: 0 !important;
      list-style-position: inside !important;
    }

    .code-list li {
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
    }

    /* Fix code type headers */
    .codeTypeHeader {
      white-space: normal !important;
      overflow: visible !important;
      text-overflow: unset !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }
  }
</style>
